import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { FormFeedback } from 'reactstrap';
import { get } from 'lodash';

import { validationCheckErrorType } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { checkHadError } from 'utils';

import { ReactComponent as ChevronDown } from 'assets/images/Chervon.svg';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDown />
  </components.DropdownIndicator>
);

const AppSelectValidation = (props) => {
  const {
    isHiddenClearable,
    isHiddenSearchable,
    isMulti,
    isGroup,
    isShowError,
    isDisabled,

    checkErrorType,
    keyName,
    name,
    id,
    placeholder,
    selectedClassName,
    subTitle,
    value,
    isUsePureValue,
    fontSize,

    validation,
    options,

    isLoading,
    onBlur,
    onChange,
    onChangeMulti,
    onMenuScrollToBottom,
    onMenuOpen,
    onInputChange,
    inputValue,
    filterOption,
  } = props;

  const keyCheckError = useMemo(() => keyName || name, [keyName, name]);

  const isHadError = useMemo(
    () => checkHadError(validation, keyCheckError, checkErrorType),
    [validation, keyCheckError, checkErrorType],
  );

  const selectInvalid = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        border: '1px solid var(--bookese-danger-color) !important',
        boxShadow: 'var(--bookese-danger-color) !important',
      }),

      indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
      }),
    }),
    [],
  );

  const stylesCustom = useMemo(
    () => ({
      control: (provided, state) => ({
        ...provided,
        boxShadow: state.isFocused ? 'var(--bookese-blue-100)' : '',
        borderColor: state.isFocused
          ? 'var(--bookese-blue-100) !important'
          : 'var(--bookese-gray-200)',
        fontSize: `${fontSize} !important`,
        minHeight: '36px',
        height: 'fit-content',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      }),

      singleValue: (provided) => ({
        ...provided,
        fontSize: `${fontSize} !important`,
      }),

      placeholder: (provided) => ({
        ...provided,
        fontSize: `${fontSize} !important`,
      }),

      input: (provided) => ({
        ...provided,
        fontSize: `${fontSize} !important`,
      }),

      option: (provided) => ({
        ...provided,
        cursor: 'pointer',
      }),

      indicatorSeparator: (provided) => ({
        ...provided,
        display: 'none',
      }),
    }),
    [fontSize],
  );

  const finalValue = React.useMemo(() => {
    switch (true) {
      case isUsePureValue:
      case isMulti: {
        return value;
      }

      case isGroup: {
        const selectedOption = options
          .flatMap((group) => group.options)
          .find((item) => item.value === value);

        return selectedOption || null;
      }

      default: {
        return options.filter?.((item) => item.value === value);
      }
    }
  }, [isMulti, isGroup, isUsePureValue, options, value]);

  const onChangeCallback = React.useCallback(
    (e) => {
      onChange({ ...e, name, id });
    },
    [id, name, onChange],
  );

  const onBlurCallback = React.useCallback(
    (e) => {
      if (validation.handleBlur) {
        validation.handleBlur(e);
      }

      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur, validation],
  );

  return (
    <>
      <Select
        options={options}
        value={finalValue}
        name={name}
        id={id}
        inputId={id || name}
        isMulti={isMulti}
        classNamePrefix="select2-selection"
        className={`basic-multi-select ${selectedClassName}`}
        isSearchable={isHiddenSearchable}
        isClearable={isHiddenClearable}
        placeholder={placeholder}
        onBlur={onBlurCallback}
        onChange={isMulti ? onChangeMulti : onChangeCallback}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onMenuOpen={onMenuOpen}
        onInputChange={onInputChange}
        inputValue={inputValue}
        filterOption={filterOption}
        isLoading={isLoading}
        noOptionsMessage={({ option }) => !option && 'Không có lựa chọn'}
        styles={isShowError && isHadError ? selectInvalid : stylesCustom}
        components={{ DropdownIndicator }}
        invalid={isShowError && isHadError}
        isDisabled={isDisabled}
      />

      {subTitle && <div className="Selection-SubTitle">{subTitle}</div>}

      {isShowError && isHadError && (
        <FormFeedback type="invalid" className="d-block font-size-12">
          {get(validation.errors, keyCheckError, '')}
        </FormFeedback>
      )}
    </>
  );
};

AppSelectValidation.propTypes = {
  isHiddenClearable: PropTypes.bool,
  isHiddenSearchable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isGroup: PropTypes.bool,
  isShowError: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,

  checkErrorType: PropTypes.oneOf([
    '',
    validationCheckErrorType.ERR_ONLY,
    validationCheckErrorType.TOUCHED_ONLY,
  ]),
  selectedClassName: PropTypes.string,
  keyName: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  subTitle: PropTypes.string,
  fontSize: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Object),
  ]),
  isUsePureValue: PropTypes.bool,

  placeholder: PropTypes.string,

  validation: PropTypes.instanceOf(Object),
  options: PropTypes.instanceOf(Array).isRequired,

  onBlur: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onChangeMulti: PropTypes.func,
  onMenuScrollToBottom: PropTypes.func,
  onMenuOpen: PropTypes.func,
  onInputChange: PropTypes.func,
  inputValue: PropTypes.string,
  filterOption: PropTypes.func,
};

AppSelectValidation.defaultProps = {
  isHiddenClearable: false,
  isHiddenSearchable: false,
  isMulti: false,
  isGroup: false,
  isShowError: true,
  isDisabled: false,
  isLoading: false,

  checkErrorType: '',
  keyName: '',
  name: '',
  id: '',
  fontSize: '13px',
  placeholder: 'Chọn',
  selectedClassName: '',
  subTitle: '',
  value: '',
  isUsePureValue: false,

  validation: INITIAL_VALUE.OBJECT,

  onBlur: INITIAL_VALUE.FUNCTION,
  onChangeMulti: INITIAL_VALUE.FUNCTION,
  onMenuScrollToBottom: INITIAL_VALUE.FUNCTION,
  onMenuOpen: INITIAL_VALUE.FUNCTION,
  onInputChange: undefined,
  inputValue: undefined,
  filterOption: undefined,
};

export default memo(AppSelectValidation);
